import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { FilterList, Close, Refresh, FilterAlt } from "@mui/icons-material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import useInventory from "../../../hooks/useInventory";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import { saveFile } from "../../../utils/common";
import ToolBar from "../../../components/Toolbar";
import DatePicker from "react-multi-date-picker";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Inventory</Typography>
      <Typography>User Stock</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

function UserStock() {
  const { getUserStockReport, exportInventoryReport, isLoading } = useInventory();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");
  const dispatch = useDispatch();

  // Filter States
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUserName, setFilterUserName] = useState("");
  const [filterMaterialName, setFilterMaterialName] = useState("");
  const [filterLastIssueDate, setFilterLastIssueDate] = useState("");
  const [filterLastReturnDate, setFilterLastReturnDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState([]);

  const buildFilters = () => {
    const activeFilters = [];
    if (filterUserName.trim()) {
      activeFilters.push({
        fieldName: "userName",
        fieldValue: filterUserName.trim(),
        op: "like",
        dataType: "string",
        condition: "and",
        isJoin: false,
      });
    }
    if (filterMaterialName.trim()) {
      activeFilters.push({
        fieldName: "materialName",
        fieldValue: filterMaterialName.trim(),
        op: "like",
        dataType: "string",
        condition: "and",
        isJoin: false,
      });
    }
    if (filterLastIssueDate) {
      activeFilters.push({
        fieldName: "lastIssueDate",
        fieldValue: filterLastIssueDate,
        op: "eq",
        dataType: "date",
        condition: "and",
        isJoin: false,
      });
    }
    if (filterLastReturnDate) {
      activeFilters.push({
        fieldName: "lastReturnDate",
        fieldValue: filterLastReturnDate,
        op: "eq",
        dataType: "date",
        condition: "and",
        isJoin: false,
      });
    }
    return activeFilters;
  };

  const handleApplyFilters = () => {
    const activeFilters = buildFilters();
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters(activeFilters);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setFilterUserName("");
    setFilterMaterialName("");
    setFilterLastIssueDate("");
    setFilterLastReturnDate("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters([]);
    setFilterOpen(false);
  };

  const handleDownloadFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const resBlob = await exportInventoryReport("user-stock", fileType, {
        filters: appliedFilters,
      });
      const filename = `user_stock_${new Date().toISOString().slice(0, 10)}.${fileType}`;
      await saveFile(resBlob, filename);
      dispatch(showSuccessSnackbar(`Report exported successfully as ${fileType.toUpperCase()}!`));
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to export report"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const fetchUserStock = async () => {
    try {
      const payload = {
        pagination: {
          pageNumber: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
        },
        filters: appliedFilters,
        sortFields: [],
      };
      const res = await getUserStockReport(payload);
      if (res) {
        setRows(res.data || []);
        setRowCount(res.paging?.totalNumberOfRecords || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchUserStock();
  }, [paginationModel, appliedFilters]);

  const columns = [
    { field: "userName", headerName: "User", width: 250 },
    { field: "materialName", headerName: "Material", width: 220 },
    { field: "quantity", headerName: "Available Quantity", width: 180, type: "number" },
    { field: "lastIssueDate", headerName: "Last Issue Date", width: 200 },
    { field: "lastReturnDate", headerName: "Last Return Date", width: 200 },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Card mb={6}>
        <CardHeader
          title="User Stock"
          subheader={breadCrump()}
          action={
            <Box display="flex" gap={1} alignItems="center" sx={{ mt: 2, mr: 2 }}>
              <ToolBar
                refreshPage={fetchUserStock}
                openFilterDialog={() => setFilterOpen(true)}
                downloadFile={handleDownloadFile}
                isLoadingDownload={isDownloading}
                downloadFileType={downloadType}
              />
            </Box>
          }
        />
        <CardContent>
          <Paper elevation={0}>
            <div style={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => `${row.userName}-${row.materialName}`}
                paginationMode="server"
                rowCount={rowCount}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                loading={isLoading}
                disableRowSelectionOnClick
              />
            </div>
          </Paper>
        </CardContent>
      </Card>

      {/* Filters Dialog */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">Advance Filter</Typography>
          <IconButton onClick={() => setFilterOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Box display="flex" flexDirection="column" gap={4}>
            <TextField
              label="Username"
              variant="outlined"
              value={filterUserName}
              onChange={(e) => setFilterUserName(e.target.value)}
              placeholder="e.g. john_doe"
              fullWidth
            />
            <TextField
              label="Material Name"
              variant="outlined"
              value={filterMaterialName}
              onChange={(e) => setFilterMaterialName(e.target.value)}
              placeholder="e.g. Fiber, ONU"
              fullWidth
            />
            <DatePicker
              containerStyle={{ width: "100%" }}
              portal
              zIndex={10000}
              value={filterLastIssueDate}
              onChange={(date) => setFilterLastIssueDate(date ? date.format("YYYY-MM-DD") : "")}
              format="YYYY/MM/DD"
              render={
                <TextField
                  fullWidth
                  label="Last Issue Date"
                  variant="outlined"
                  InputLabelProps={{ shrink: !!filterLastIssueDate }}
                />
              }
            />
            <DatePicker
              containerStyle={{ width: "100%" }}
              portal
              zIndex={10000}
              value={filterLastReturnDate}
              onChange={(date) => setFilterLastReturnDate(date ? date.format("YYYY-MM-DD") : "")}
              format="YYYY/MM/DD"
              render={
                <TextField
                  fullWidth
                  label="Last Return Date"
                  variant="outlined"
                  InputLabelProps={{ shrink: !!filterLastReturnDate }}
                />
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClearFilters} variant="outlined" startIcon={<Refresh />}>
            Reset
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="contained"
            color="primary"
            startIcon={<FilterAlt />}
          >
            Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserStock;
